import React, { useState, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import { TreeDataModule } from 'ag-grid-enterprise';
import { OmniDropdownComponent, OmniIconComponent, Utils, OverBudgetWarningModal } from '../../../../../shared';
import { PlacementDetailsTableService } from "../../../../placements";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule, TreeDataModule]);

/**
 * CreateMediaOrderWizard - A multi-step wizard component for creating media orders
 * 
 * This component provides a 3-step wizard interface:
 * Step 1: Taxonomy-compliant order naming
 * Step 2: Channel and publisher budget selection with inline editing
 * Step 3: Order details confirmation
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls wizard visibility
 * @param {Function} props.onClose - Callback when wizard is closed
 * @param {Function} props.onSubmit - Callback when order creation is submitted
 * @param {Object} props.campaignTaxonomyData - Campaign taxonomy configuration and fields
 * @param {Array} props.campaignData - Campaign data including dates, budget, and channels
 * @param {Object} props.systemParametersData - System parameters including currency settings
 * @param {Array} props.clientCodes - Available client codes for selection
 */
const CreateMediaOrderWizard = ({ isOpen, onClose, onSubmit, campaignTaxonomyData, campaignData, systemParametersData, clientCodes }) => {

    const [currentStep, setCurrentStep] = useState(1);
    const gridRef = useRef(null);
    const [taxonomyPreview, setTaxonomyPreview] = useState('');
    const [selectedTotal, setSelectedTotal] = useState(0);
    const [selectedChannelData, setSelectedChannelData] = useState([]);
    const [editingCells, setEditingCells] = useState({}); // Track which cells are being edited
    const [editedData, setEditedData] = useState({}); // Store edited values
    const [showBudgetWarning, setShowBudgetWarning] = useState(false);
    const [overBudgetItems, setOverBudgetItems] = useState([]);

    const localCurrency = campaignData[0]?.local_currency_name;
    const isLocal = systemParametersData?.data?.isLocal;

    /**
     * Extracts and parses parent campaign's taxonomy data
     * 
     * Retrieves the campaign's taxonomy information and splits it into a structured object
     * using the taxonomy schema. This data is used to pre-populate form fields.
     * 
     * @returns {Object} Parsed taxonomy data object with field values
     */
    const getParentTaxonomyData = () => {
        let campaign = campaignData[0];
        let taxonomyJson = campaign?.taxonomy ? campaign?.taxonomy : null;
        let campaignTaxonomy = campaign?.campaign_taxonomy?.split(':|');
        let taxonomyName = campaignTaxonomy[0];
        let taxonomySchema = campaign?.taxonomy_schema ? campaign?.taxonomy_schema : null;
        let taxonomyData = campaignTaxonomyData?.fields;
        
        return PlacementDetailsTableService.splitPlacementStringIntoSchemaObject(taxonomyName, taxonomySchema, taxonomyData, '', null, taxonomyJson);
    };
    
    /**
     * Initializes form data with default values from campaign data
     * 
     * Creates initial form state including:
     * - Flight dates from campaign
     * - Client code
     * - Dynamic taxonomy fields with values from parent campaign or "NU" (Not Used)
     * 
     * @returns {Object} Initial form data object with all required fields
     */
    const initializeFormData = () => {
        const initialData = {
            flight: {
                startDate: campaignData[0]?.start_date || '',
                endDate: campaignData[0]?.end_date || ''
            },
            planned: '',
            ordered: '',
            costType: '',
            clientCode: campaignData[0]?.client_code,
            orderBudget: ''
        };
        let taxonomyData = getParentTaxonomyData();
        
        // Add dynamic fields from campaignTaxonomyData
        if (campaignTaxonomyData?.fields) {
            campaignTaxonomyData.fields.forEach(field => {
                initialData[field.alias || field.name] = taxonomyData[field.alias || field.name] || "NU";
            });
        }
        
        return initialData;
    };

    const [formData, setFormData] = useState(() => initializeFormData());

    /**
     * Transforms campaign channel data into AG Grid tree structure
     * 
     * Creates a hierarchical data structure with:
     * - Parent nodes: Channels with aggregated budgets and date ranges
     * - Child nodes: Publishers with individual budgets and dates
     * 
     * Handles currency conversion based on local/USD settings and formats
     * dates to MM/DD/YY format for display.
     * 
     * @returns {Array} Array of channel objects with nested publisher data
     */
    const transformChannelData = () => {
        const channels = campaignData[0]?.media_attributes?.channels;
        if (!channels || !Array.isArray(channels)) return [];

        return channels.map((channel, channelIndex) => {
            // Format date range for channel
            const channelStartDate = channel.channel_startdate ? new Date(channel.channel_startdate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '';
            const channelEndDate = channel.channel_enddate ? new Date(channel.channel_enddate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '';
            const channelDateRange = channelStartDate && channelEndDate ? `${channelStartDate} - ${channelEndDate}` : '';

            // Transform publishers (nestedData)
            const publishers = channel.nestedData?.map((publisher, pubIndex) => {
                const pubStartDate = publisher.publisher_start_date ? new Date(publisher.publisher_start_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '';
                const pubEndDate = publisher.publisher_end_date ? new Date(publisher.publisher_end_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '';
                const pubDateRange = pubStartDate && pubEndDate ? `${pubStartDate} - ${pubEndDate}` : publisher.flight || '';

                return {
                    id: `publisher-${channelIndex}-${pubIndex}`,
                    channel: publisher.publisher_display_name || publisher.publisher_name || publisher.description,
                    date: pubDateRange,
                    availablePlannedSpend: localCurrency && isLocal ? publisher.publisher_budget_in_pmds_local : publisher.publisher_budget_in_pmds || 0,
                    isChannel: false,
                    // Keep original data for reference
                    originalData: publisher
                };
            }) || [];

            return {
                id: `channel-${channelIndex}`,
                channel: channel.channel_display_name || channel.channel_name,
                date: channelDateRange,
                availablePlannedSpend: localCurrency && isLocal ? channel.channel_total_budget_in_pmds_local : channel.channel_total_budget_in_pmds || 0,
                isChannel: true,
                nestedData: publishers,
                // Keep original data for reference
                originalData: channel
            };
        });
    };

    const gridChannelData = transformChannelData();

    const totalSteps = 3;

    /**
     * Handles input field changes in Step 1 (taxonomy fields)
     * 
     * Updates form data, extracting the ID from dropdown selections
     * or using the value directly for text inputs.
     * 
     * @param {string} field - Field name to update
     * @param {string|Object} value - New value or dropdown selection object with id property
     */
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value?.id ? value.id : value
        }));
    };

    /**
     * Handles flight date changes (start/end date)
     * 
     * Updates the nested flight object within form data.
     * 
     * @param {string} field - Either 'startDate' or 'endDate'
     * @param {string} value - Date value in ISO format (YYYY-MM-DD)
     */
    const handleFlightChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            flight: {
                ...prev.flight,
                [field]: value
            }
        }));
    };

    /**
     * Handles navigation to next step with budget validation
     * 
     * For Step 2, performs over-budget validation:
     * - Compares edited budget values against original values
     * - Shows warning modal if any selected publishers exceed their original budget
     * - Prevents navigation if over-budget items are detected
     * 
     * For other steps, proceeds to next step normally.
     */
    const handleNext = () => {
        if (currentStep === 2) {
            // Check if any edited budgets exceed original values
            const overBudget = [];
            selectedChannelData.forEach(item => {
                if (!item.isChannel && editedData[item.id] !== undefined) {
                    const originalValue = item.availablePlannedSpend;
                    const editedValue = editedData[item.id];
                    if (editedValue > originalValue) {
                        overBudget.push({
                            name: item.channel,
                            overAmount: editedValue - originalValue
                        });
                    }
                }
            });

            if (overBudget.length > 0) {
                setOverBudgetItems(overBudget);
                setShowBudgetWarning(true);
                return;
            }
        }

        if (currentStep < totalSteps) {
            setCurrentStep(prev => prev + 1);
        }
    };

    /**
     * Handles navigation to previous step
     * Decrements the current step if not on the first step.
     */
    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
        }
    };

    /**
     * Handles final form submission
     * Calls the parent onSubmit callback with complete form data.
     */
    const handleSubmit = () => {
        onSubmit(formData);
    };

    /**
     * AG Grid ready callback - restores previously selected rows
     * 
     * When the grid initializes or re-renders, this callback restores
     * any previously selected channels/publishers to maintain selection
     * state across renders.
     * 
     * @param {Object} params - AG Grid params object with api reference
     */
    const onGridReady = React.useCallback((params) => {
        if (selectedChannelData.length > 0) {
            const selectedIds = selectedChannelData.map(item => item.id);
            params.api.forEachNode((node) => {
                if (selectedIds.includes(node.data?.id)) {
                    node.setSelected(true);
                }
            });
        }
    }, [selectedChannelData]);

    /**
     * Generates taxonomy-compliant order name preview
     * 
     * Concatenates form field values with their configured delimiters
     * according to the taxonomy schema. Hidden fields are excluded.
     * The last field doesn't include a delimiter.
     * 
     * @returns {string} Formatted taxonomy preview string
     */
    const generateTaxonomyPreview = () => {
        if (!campaignTaxonomyData?.fields) return '';
        
        const parts = campaignTaxonomyData.fields.map((field, index) => {
            const value = formData[field.alias || field.name];
            if (value && !field.hidden) {
                if(index === campaignTaxonomyData.fields.length - 1){
                    return value;
                }
                return value + field.delimiter_value;    
            }
            return '';
        });
        
        return parts.filter(p => p).join('');
    };

    // Update preview whenever formData changes
    React.useEffect(() => {
        setTaxonomyPreview(generateTaxonomyPreview());
    }, [formData]);

    /**
     * Toggles edit mode for a budget cell
     * 
     * Only one cell can be in edit mode at a time. Clicking an editing cell
     * closes it, clicking a non-editing cell opens it and closes others.
     * Forces grid refresh after state change to re-render the cell.
     * 
     * @param {string} rowId - Unique identifier of the row (publisher/channel ID)
     */
    const handleEditClick = (rowId) => {
        setEditingCells(prev => {
            const isCurrentlyEditing = prev[rowId];
            // If clicking the same cell that's being edited, close it
            // Otherwise, set only this cell to editing mode
            const newState = isCurrentlyEditing ? {} : { [rowId]: true };
            
            // Force grid to refresh the cell after state update
            setTimeout(() => {
                if (gridRef.current?.api) {
                    gridRef.current.api.refreshCells({ force: true });
                }
            }, 0);
            return newState;
        });
    };

    /**
     * Handles budget value changes in edit mode
     * 
     * Parses the input value, removing non-numeric characters except decimal points,
     * and stores the numeric value. Defaults to 0 if parsing fails.
     * 
     * @param {string} rowId - Row identifier where value is being edited
     * @param {string} value - Raw input value (may contain currency formatting)
     */
    const handleSpendChange = (rowId, value) => {
        const numericValue = parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
        setEditedData(prev => ({
            ...prev,
            [rowId]: numericValue
        }));
    };

    /**
     * Retrieves the current value for a row (edited or original)
     * 
     * Checks if an edited value exists for the row, otherwise returns
     * the original value from the data source.
     * 
     * @param {string} rowId - Row identifier
     * @param {number} originalValue - Original budget value from data
     * @returns {number} Current value (edited if exists, otherwise original)
     */
    const getCurrentValue = (rowId, originalValue) => {
        return editedData[rowId] !== undefined ? editedData[rowId] : originalValue;
    };

    /**
     * Custom cell renderer for the Available Planned Spend column
     * 
     * Renders either:
     * - An editable input field (when cell is in edit mode)
     * - A formatted currency display with edit icon (when in view mode)
     * 
     * Features:
     * - Currency formatting based on campaign settings (local or USD)
     * - Inline editing with Enter key or blur to save
     * - Visual distinction for zero values (grayed out)
     * - Edit icon that triggers edit mode
     * 
     * @param {Object} props - AG Grid cell renderer props
     * @param {Object} props.data - Row data object
     * @param {number} props.value - Cell value (original budget amount)
     * @returns {JSX.Element} Rendered cell content
     */
    const AvailablePlannedSpendRenderer = (props) => {
        const rowId = props.data?.id;
        const isEditing = editingCells[rowId];
        const currentValue = getCurrentValue(rowId, props.value);
        const currency = localCurrency && isLocal ? localCurrency : 'USD';

        const formattedValue = currentValue?.toLocaleString('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }) || '$0.00';

        const isZeroValue = props.value === 0;

        return (
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'flex-end',
                gap: '8px',
                height: '100%',
                width: '100%'
            }}>
                {isEditing ? (
                    <input
                        type="text"
                        className="ag-cell-edit-input"
                        style={{
                            width: '100%',
                            textAlign: 'right',
                            padding: '4px 8px',
                            border: '1px solid #0066cc',
                            borderRadius: '4px',
                            fontSize: '13px'
                        }}
                        value={currentValue}
                        onChange={(e) => handleSpendChange(rowId, e.target.value)}
                        onBlur={() => {
                            setEditingCells({});
                            setTimeout(() => {
                                if (gridRef.current?.api) {
                                    gridRef.current.api.refreshCells({ force: true });
                                }
                            }, 0);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                setEditingCells({});
                                setTimeout(() => {
                                    if (gridRef.current?.api) {
                                        gridRef.current.api.refreshCells({ force: true });
                                    }
                                }, 0);
                            }
                        }}
                        autoFocus
                    />
                ) : (
                    <>
                        <span style={{ 
                            flex: 1, 
                            textAlign: 'right',
                            color: isZeroValue ? '#d3d3d3' : 'inherit'
                        }}>
                            {formattedValue}
                        </span>
                        <div 
                            style={{
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#666',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(rowId);
                            }}
                        >
                            <OmniIconComponent
                                icon-id="omni:interactive:edit"
                            />
                        </div>
                    </>
                )}
            </div>
        );
    };

    // Column definitions for AG Grid (defined outside render to avoid recreation)
    const columnDefs = React.useMemo(() => [
        {
            field: 'channel',
            headerName: 'CHANNEL',
            cellRenderer: 'agGroupCellRenderer',
            showRowGroup: true,
            cellRendererParams: {
                suppressCount: true,
            },
            flex: 2
        },
        {
            field: 'date',
            headerName: 'DATE',
            flex: 1.5,
            cellStyle: (params) => {
                if (params.data?.isChannel && (!params.data?.date || params.data?.availablePlannedSpend === 0)) {
                    return { color: '#d3d3d3' };
                }
                return null;
            }
        },
        {
            field: 'availablePlannedSpend',
            headerName: 'AVAILABLE PLANNED SPEND',
            flex: 1.5,
            cellRenderer: AvailablePlannedSpendRenderer,
            
        }
    ], [localCurrency, isLocal, editingCells, editedData]);

    const defaultColDef = React.useMemo(() => ({
        sortable: false,
        resizable: true,
        filter: false,
        suppressHeaderMenuButton: true
    }), []);

    const channelTableTheme = React.useMemo(() => themeQuartz.withParams({
        headerRowBorder: false,
        rowBorder: false,
        spacing: 8,
        wrapperBorder: false,
    }), []);

    /**
     * Formats date value for display in date inputs
     * 
     * Handles ISO datetime strings by extracting the date portion,
     * and uses Utils.formatDateObject for final formatting.
     * 
     * @param {string} value - Date value (may include time component)
     * @returns {string} Formatted date string
     */
    const getDate = (value) => {
        const date = value && value.includes('T') ? value.split('T')[0] : value === 'string' ? '' : value;
        return Utils.formatDateObject(date);
    }

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="wizard-step">
                        <h2>Fill in the fields below to create a taxonomy compliant name for this order. You can update this later if needed.</h2>
                        
                        {/* Taxonomy Preview */}
                        {taxonomyPreview && (
                            <div className="taxonomy-preview">
                                <div className="preview-label">Preview:</div>
                                <div className="preview-text">
                                    <i className="omni-icon-info"></i>
                                    {taxonomyPreview}
                                </div>
                            </div>
                        )}

                        {/* Dynamic Fields from campaignTaxonomyData - Grid Layout */}
                        <div className="form-fields-grid">
                            {campaignTaxonomyData?.fields?.map((field, index) => (
                                <div className="form-group" key={field.field_name || index}>
                                    <label>
                                        {field.mandatory ? '*' : ''}{field.alias || field.name}
                                    </label>
                                    
                                    {field.field_type === 1 ? (
                                        // Dropdown field
                                        <OmniDropdownComponent
                                            typeahead
                                            searchindropdown
                                            hidefooter
                                            placeholder="Please select"
                                            onDropdownChange={(ev) => handleInputChange(field.alias || field.name, ev.detail)}
                                            value={formData[field.alias || field.name] ? [formData[field.alias || field.name]] : []}
                                            options={field.fielditems?.map(item => ({
                                                value: item.name,
                                                id: item.code
                                            })) || []}
                                        />
                                    ) : (
                                        // Text input field
                                        <input
                                            type="text"
                                            value={formData[field.alias || field.name] || ''}
                                            onChange={(e) => handleInputChange(field.alias || field.name, e.target.value)}
                                            placeholder={field.alias || field.name}
                                            required={field.is_required}
                                            maxLength={field.character_limit || undefined}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="wizard-step">
                        <h2>Set up an order container from available planned spend.</h2>
                        
                        <div style={{ height: '400px', width: '100%', marginTop: '24px' }}>
                            <AgGridReact
                                ref={gridRef}
                                theme={channelTableTheme}
                                columnDefs={columnDefs}
                                rowData={gridChannelData}
                                defaultColDef={defaultColDef}
                                treeData={true}
                                treeDataChildrenField="nestedData"
                                getRowId={(params) => params.data.id}
                                rowSelection={{
                                    mode: "multiRow",
                                    groupSelects: "descendants",
                                    checkboxLocation: 'autoGroupColumn',
                                    hideDisabledCheckboxes: true,
                                }}
                                onGridReady={onGridReady}
                                onSelectionChanged={(event) => {
                                    const selectedNodes = event.api.getSelectedNodes();
                                    const selectedData = selectedNodes.map(node => node.data);
                                    
                                    // Store full selected data objects for API submission
                                    setSelectedChannelData(selectedData);
                                    
                                    // Calculate total from selected publishers only (not parent channels)
                                    // Use edited values if available
                                    const total = selectedData
                                        .filter(item => !item.isChannel) // Only publishers
                                        .reduce((sum, item) => {
                                            const value = getCurrentValue(item.id, item.availablePlannedSpend);
                                            return sum + (value || 0);
                                        }, 0);
                                    setSelectedTotal(total);
                                }}
                                groupDefaultExpanded={-1}
                                groupDisplayType={"custom"}
                                headerHeight={40}
                                rowHeight={40}
                            />
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="wizard-step">
                        <h2>Confirm order details. You can update this later if needed.</h2>
                        
                        <div className="form-fields-horizontal-centered">
                            <div className="form-group-horizontal">
                                <label>Client Code:</label>
                                <OmniDropdownComponent
                                    typeahead
                                    searchindropdown
                                    hidefooter
                                    placeholder="Select Client Code"
                                    onDropdownChange={(ev) => handleInputChange('clientCode', ev.detail)}
                                    value={formData.clientCode ? [formData.clientCode] : []}
                                    options={clientCodes}
                                />
                            </div>

                            <div className="form-group-horizontal">
                                <label>Date Range:</label>
                                <div className="date-range-container">
                                    <input
                                        type="date"
                                        id="start-date"
                                        className='input'
                                        value={formData.flight.startDate || ''}
                                        data-date={getDate(formData.flight.startDate) || 'Start Date'}
                                        onChange={(e) => handleFlightChange('startDate', e.target.value)}
                                        min={campaignData[0]?.start_date || ''}
                                        max={campaignData[0]?.end_date || ''}
                                    />
                                    <span className="date-separator">—</span>
                                    <input
                                        type="date"
                                        id="end-date"
                                        className='input'
                                        value={formData.flight.endDate || ''}
                                        data-date={getDate(formData.flight.endDate) || 'End Date'}
                                        onChange={(e) => handleFlightChange('endDate', e.target.value)}
                                        min={campaignData[0]?.start_date || ''}
                                        max={campaignData[0]?.end_date || ''}
                                    />
                                </div>
                            </div>

                            <div className="form-group-horizontal">
                                <label>Order Budget:</label>
                                <input
                                    type="text"
                                    className='input'
                                    value={selectedTotal.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: localCurrency && isLocal ? localCurrency : 'USD',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}
                                    readOnly
                                    placeholder="$0.00"
                                />
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="create-media-order-wizard-overlay">
            <div className="create-media-order-wizard">
                <div className="wizard-header">
                    <h2>Set Up New Order</h2>
                    <div className="wizard-header-right">
                        <span className="step-indicator">{currentStep} of {totalSteps}</span>
                        <button className="tertiary" onClick={onClose}>
                            <OmniIconComponent icon-id="omni:interactive:close" />
                        </button>
                    </div>
                </div>

                <div className="wizard-content">
                    {renderStepContent()}
                </div>

                {currentStep === 2 && (
                    <div className="wizard-total-section">
                        <span className="total-label">Total:</span>
                        <span className="total-amount">
                            {selectedTotal.toLocaleString('en-US', {
                                style: 'currency',
                                currency: localCurrency && isLocal ? localCurrency : 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </span>
                        {/* <OmniIconComponent 
                            icon-id="omni:informative:info" 
                            style={{ marginLeft: '8px', fontSize: '16px', cursor: 'pointer', color: '#999' }}
                        /> */}
                    </div>
                )}

                <div className="wizard-footer">
                    {currentStep === 1 && (
                        <div className="wizard-actions-left">
                            <span className="required-note">* Required fields</span>
                        </div>
                    )}
                    <div className="wizard-actions-right">
                        <button className="tertiary" onClick={onClose}>
                            Cancel
                        </button>
                        {currentStep > 1 && (
                            <button 
                                className="secondary"
                                onClick={handlePrevious}
                            >
                                Back
                            </button>
                        )}
                        {currentStep < totalSteps ? (
                            <button onClick={handleNext}>
                                Next
                            </button>
                        ) : (
                            <button onClick={handleSubmit}>
                                Create Media Order
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Over Budget Warning Modal */}
            <OverBudgetWarningModal
                isOpen={showBudgetWarning}
                onClose={() => setShowBudgetWarning(false)}
                onContinue={() => {
                    setShowBudgetWarning(false);
                    setCurrentStep(prev => prev + 1);
                }}
                overBudgetItems={overBudgetItems}
                localCurrency={localCurrency}
                isLocal={isLocal}
                warningMessage="You're about to create a Prisma campaign (media order container) that exceeds the planned media budget in Planit."
            />
        </div>
    );
};

export default CreateMediaOrderWizard;
